import { EventEmitter, Injectable } from "@angular/core";
import { EMPTY, Observable, Subject } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";
import { throwError } from "rxjs";
import { Uris } from "./Uris";
import { map, debounceTime, switchMap } from "rxjs/operators";
import { submitCycle } from "../Clients/cycles-plantings/interfaces/cycle.interface";

@Injectable({
  providedIn: "root"
})
export class StorageService {
  permissions = false;
  public expandsView = false;
  public preference = { id: "", personal: "", user: "" };
  public view_modules = false;
  public localValue: localValue = {
    /* Configuraciones para el admin  */
    theme: "D",
    mode: "" /* Modo de vista 0 ---> admin 1 ---> cliente */,
    client: "" /* Almacena todo el objeto del cliente que solicito el acceso */,
    language: "spanish" /* lenguaje una vez cambiado */,
    /* Configuraciones para admin y cliente */
    farm_id: "" /* Granja seleccionada si esta en modo cliente */,
    isCollapsedText: false /* Quita los textos del sidebar */,
    sidebar_collapsed_sections: [] /* Secciones del sidebar plegadas (claves i18n) */,
    current_url: "" /* Almacena la ultima url */,
    mode_table: false /* Muestra listas o tablas */,
    keep_devices_selected: false /* Mantener equipos seleccionados al cerrar modales */,
    visible_filters: {} /* Filtros mostrados en la barra principal (compartidos entre pantallas) */,
    required_pond_testing:
      false /* Cache de configuración de granja: requiere test de monitor antes de alimentar */,
    /* Querys almacenados como cliente solo INPUTS */
    query_client: {
      current_url: "",
      pond_id: "",
      orderingAdvance: "",
      planting_id: "",
      harvested_state: "",
      state: ""
    },
    /* Querys almacenados como admin solo INPUTS */
    query_admin: {
      current_url: "",
      client_id: "",
      farm_id: "",
      pond_id: "",
      orderingAdvance: ""
    }
  };
  public checks_parametry_preference: preferenceChecks = {} as preferenceChecks;

  public size: number = 0;
  public limit = 6;

  public destroy$ = new Subject<void>();

  /** Avisos de filtros cambiados; se guardan en el servidor con debounce (ver saveQueryFilters). */
  private persistFilters$ = new Subject<void>();
  /** Momento del ultimo cambio de filtros y del ultimo guardado completo de preferencias. */
  private lastFilterChange = 0;
  private lastPreferenceSave = 0;
  /** Ultimo personal_web conocido en el servidor (JSON), para no mandar PATCH sin cambios. */
  private savedPersonalWeb: string | null = null;

  constructor(public router: Router, public toastr: ToastrService, private http: HttpClient) {
    // Los filtros (empresa, granja, estanque...) se comparten entre pantallas en
    // localValue; se guardan en preferencias para que sobrevivan a recargas.
    // Si otro guardado completo (cambio de modo, granja...) ya salio despues del ultimo
    // cambio de filtros, ya los incluye: no se manda un PATCH extra que compita con el.
    this.persistFilters$.pipe(debounceTime(1000)).subscribe(() => {
      if (this.lastPreferenceSave > this.lastFilterChange) return;
      this.persistQueryFilters();
    });
  }

  /**
   * Guarda en el servidor solo el query de filtros del modo actual. Se parte de
   * las preferencias del servidor para no pisar lo que otro dispositivo haya
   * cambiado (modo, granja, tema...) con la copia local.
   * @returns void
   */
  private persistQueryFilters(): void {
    /* Sin sesión (p. ej. tras cerrar sesión) no hay preferencias que guardar */
    if (!localStorage.getItem("token")) return;
    const { mode } = this.localValue;
    const key = (mode === 0 || !mode) && this.validateSoloAdmin() ? "query_admin" : "query_client";
    const query = this.localValue[key];
    this.getPreferences("not_save")
      .pipe(
        switchMap((resp: any) => {
          const server = resp && resp.personal_web ? resp.personal_web : this.localValue;
          const visible_filters = this.filterVisibility;
          return this.editPreferences({ ...server, [key]: query, visible_filters }) || EMPTY;
        })
      )
      .subscribe({ error: () => {} });
  }

  public storageObs = {
    preference: new EventEmitter<{ id: number; personal: any; user: number }>(),
    farmId: new EventEmitter<String>(),
    mode: new EventEmitter<String>(),
    socket: new EventEmitter<String>(),
    sidebar_section: new EventEmitter<any>(),
    modalNetworkConfig: new EventEmitter<String>(),
    notifications: new EventEmitter<String>(),
    permissions: new EventEmitter<Boolean>(),
    reload: new EventEmitter<Boolean>(),
    cycle: new EventEmitter<submitCycle>(),
    parametryPreferenceUpdated: new EventEmitter<preferenceChecks>(),
    visibleFilters: new EventEmitter<{ [key: string]: boolean }>(),
    requiredPondTestingUpdated: new EventEmitter<Boolean>()
  };

  cancelRequests(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.destroy$ = new Subject<void>();
  }
  watchStorage(key: keyof StorageService["storageObs"]): Observable<any> {
    return this.storageObs[key];
  }

  /**
   * Updates all parametry preferences and notifies subscribers.
   * @param preferences Full preference object to persist in memory.
   */
  setParametryPreferences(preferences: preferenceChecks): void {
    this.checks_parametry_preference = {
      ...preferences
    } as preferenceChecks;
    this.storageObs.parametryPreferenceUpdated.emit(this.checks_parametry_preference);
  }

  /**
   * Updates a single parametry preference and notifies subscribers.
   * @param key Preference key to update.
   * @param enabled New enabled state for the key.
   */
  setSingleParametryPreference(key: keyof preferenceChecks, enabled: boolean): void {
    this.checks_parametry_preference = {
      ...this.checks_parametry_preference,
      [key]: enabled
    } as preferenceChecks;
    this.storageObs.parametryPreferenceUpdated.emit(this.checks_parametry_preference);
  }

  /**
   * Updates the required_pond_testing farm config cache and notifies subscribers.
   * @param value New value for the required_pond_testing flag.
   */
  setRequiredPondTesting(value: boolean): void {
    this.localValue.required_pond_testing = value;
    this.storageObs.requiredPondTestingUpdated.emit(value);
  }

  setItem(
    key: keyof StorageService["storageObs"],
    data:
      | { id: number; personal: any; user: number }
      | number
      | string
      | boolean
      | localValue
      | submitCycle
      | null
  ): void {
    this.storageObs[key].emit(data);
  }

  getClientID = () => {
    const { client, mode } = this.localValue;

    let localUSer = localStorage.getItem("USER");
    let user: any = localUSer ? <any>JSON.parse(localUSer) : "";

    /* si es un administrador o subcliente */
    if (!user.client && !client && !mode) {
      return "";
    }

    /* Si es un subcliente usado como admin */
    if (mode && mode === 1 && client && !client.user.client) {
      return "";
    }

    /* Si es un cliente usado como admin */
    if (mode && mode === 1 && !this.isAdmin()) {
      return client.user.client.id;
    }

    /* Si es un cliente */
    if (!mode && !this.isAdmin()) {
      return user.client.id;
    }
  };

  isAdmin() {
    const { mode } = this.localValue;
    let localUser: string | null = localStorage.getItem("USER");
    let user: any = localUser ? <any>JSON.parse(localUser) : "";
    if (user) {
      if (user.user_type === -1 || user.user_type === 0) {
        if (!mode || mode === 0) {
          return true;
        }
      }
    }
    return false;
  }
  isSubclient() {
    let localUser: string | null = localStorage.getItem("USER");
    let user: any = localUser ? <any>JSON.parse(localUser) : "";
    if (user) {
      if (user.user_type === 2 && user.is_subclient) {
        return true;
      }
    }
    return false;
  }

  /**
   * Guarda filtros en el query del modo actual (query_admin / query_client) de
   * localValue. Los valores vacios se eliminan. Se envian al servidor de
   * inmediato con `submit` o, si no, con debounce de 1s.
   * @param form Filtros a guardar.
   * @param submit Si es true espera a guardar en el servidor.
   * @returns Promesa que se resuelve al terminar.
   */
  saveQueryFilters = async (form: any, submit = false) => {
    const { mode } = this.localValue;
    const validateQuery = () =>
      (mode === 0 || !mode) && this.validateSoloAdmin() ? "query_admin" : "query_client";
    let query = this.localValue[validateQuery()];

    let normaljson: any = query ? query : {};

    let filters = {
      ...normaljson,
      ...form
    };

    /* Eliminamos los que no tengan valores  */
    for (const key in filters) {
      const element = filters[key];
      if (element === "" || element === null) {
        delete filters[key];
      }
    }
    // Sin cambios (p. ej. la pantalla vuelve a guardar el mismo estanque al cargar): no se persiste
    const unchanged = JSON.stringify(normaljson) === JSON.stringify(filters);
    this.localValue[validateQuery()] = filters;
    if (unchanged && !submit) return;
    if (submit) {
      // Nada distinto a lo que ya tiene el servidor (p. ej. recargar en la misma url)
      if (JSON.stringify(this.localValue) === this.savedPersonalWeb) return;
      try {
        await this.editPreferences(this.localValue)?.toPromise();
      } catch (error) {}
      return;
    }
    this.lastFilterChange = Date.now();
    this.persistFilters$.next();
  };

  /**
   * Eleccion del usuario de que filtros se muestran en la barra principal de las
   * tablas. Las llaves son semanticas ("search", "client", "pond", "dates"...)
   * para que se comparta entre las pantallas que tienen el mismo filtro. Las
   * preferencias anteriores guardaban un arreglo de llaves visibles.
   * @returns Mapa llave -> visible; las llaves sin eleccion no aparecen.
   */
  get filterVisibility(): { [key: string]: boolean } {
    const saved: any = this.localValue.visible_filters;
    if (Array.isArray(saved)) {
      const map: { [key: string]: boolean } = {};
      saved.forEach((key: string) => (map[key] = true));
      return map;
    }
    return saved && typeof saved === "object" ? saved : {};
  }

  /**
   * Indica si un filtro se muestra en la barra principal.
   * @param key Llave semantica del filtro.
   * @param fallback Valor si el usuario nunca lo ha elegido (la busqueda es `true`).
   * @returns `true` si el filtro va en la barra.
   */
  isFilterVisible(key: string, fallback = false): boolean {
    const choice = this.filterVisibility[key];
    return choice === undefined ? fallback : choice;
  }

  /**
   * Muestra u oculta un filtro en la barra principal y lo guarda en
   * preferencias con el mismo debounce que los filtros (ver saveQueryFilters).
   * @param key Llave semantica del filtro.
   * @param visible `true` para mostrarlo en la barra, `false` para dejarlo solo en el panel.
   * @returns void
   */
  setFilterVisible(key: string, visible: boolean): void {
    this.localValue.visible_filters = { ...this.filterVisibility, [key]: visible };
    this.storageObs.visibleFilters.emit(this.localValue.visible_filters);
    this.lastFilterChange = Date.now();
    this.persistFilters$.next();
  }

  /**
   * Filtros del modo cliente al entrar como `client` (acceso temporal de un
   * admin a un cliente o subcliente). Solo se conservan los del cliente activo:
   * si se entra a otro cliente arrancan vacios.
   * @param client Acceso temporal devuelto por generate_temporal_access.
   * @returns query_client a guardar en preferencias.
   */
  clientQueryFor(client: any): any {
    const clientUserId = client && client.user ? client.user.id : "";
    const current = this.localValue.query_client;
    if (clientUserId && current && current.active_client === clientUserId) {
      return current;
    }
    return { current_url: "", active_client: clientUserId };
  }

  getQueryFilters(form) {
    const { mode } = this.localValue;

    let query = {};
    if ((mode === 0 || !mode) && this.validateSoloAdmin()) {
      query = this.localValue.query_admin ? { ...this.localValue.query_admin } : {};
    } else {
      let query_client = this.localValue.query_client;
      if (query_client) {
        query_client.client_id && delete query_client.client_id;
        query_client.farm_id && delete query_client.farm_id;

        query = query_client ? { ...query_client } : {};
      }
    }

    let newObject: any = {};

    for (const key in form) {
      if (query.hasOwnProperty(key)) {
        newObject[key] = query[key];
      }
    }

    return newObject;
  }

  changeQueryPropiety(
    props: Array<"client_id" | "farm_id" | "pond_id" | "orderingAdvance" | "planting_id">
  ) {
    const { mode } = this.localValue;
    const validateQuery = () =>
      (mode === 0 || !mode) && this.validateSoloAdmin() ? "query_admin" : "query_client";
    // Preferencias antiguas pueden no traer el query del modo actual
    if (!this.localValue[validateQuery()]) {
      this.localValue[validateQuery()] = {};
    }
    props.forEach((prop) => {
      this.localValue[validateQuery()][prop] = "";
    });

    return { [validateQuery()]: this.localValue[validateQuery()] };
  }

  validateSoloAdmin() {
    let localUser: string | null = localStorage.getItem("USER");
    let user: any = localUser ? <any>JSON.parse(localUser) : "";

    if ((user && user.user_type === -1) || user.user_type === 0) {
      return true;
    }
    if ((user && user.user_type === 1) || user.user_type === 2) {
      return false;
    }
    return false;
  }

  /**
   * Indica si el administrador logueado tiene el permiso admin_permissions habilitado.
   * @returns {boolean} true si el usuario logueado puede administrar permisos de otros administradores.
   */
  hasAdminPermissions(): boolean {
    let localUser: string | null = localStorage.getItem("USER");
    let user: any = localUser ? <any>JSON.parse(localUser) : "";

    return Boolean(user && user.permissions && user.permissions.admin_permissions);
  }

  /**
   * Indica si el usuario logueado puede administrar hidrófonos (super usuario o permiso admin_hydros).
   * @returns {boolean} true si el usuario puede ver y gestionar la sección de hidrófonos.
   */
  hasAdminHydrosPermission(): boolean {
    let localUser: string | null = localStorage.getItem("USER");
    let user: any = localUser ? <any>JSON.parse(localUser) : "";

    if (user && user.user_type === -1) {
      return true;
    }
    return Boolean(user && user.permissions && user.permissions.admin_hydros);
  }

  /**
   * Indica si el usuario logueado esta limitado al modo de solo alertas.
   * @returns {boolean} true si el usuario solo puede consultar alertas y no ejecutar acciones sobre los equipos.
   */
  hasAlertsOnlyPermission(): boolean {
    let localUser: string | null = localStorage.getItem("USER");
    let user: any = localUser ? <any>JSON.parse(localUser) : "";

    return Boolean(user && user.permissions && user.permissions.alerts_only);
  }

  /**
   * Indica si el administrador logueado tiene el permiso plans habilitado.
   * @returns {boolean} true si el usuario logueado puede crear, editar y habilitar/deshabilitar planes.
   */
  hasPlansPermission(): boolean {
    let localUser: string | null = localStorage.getItem("USER");
    let user: any = localUser ? <any>JSON.parse(localUser) : "";

    return Boolean(user && user.permissions && user.permissions.plans);
  }

  /**
   * Construye las cabeceras estandar de las peticiones autenticadas.
   * @param destroy Si la peticion debe cancelarse al cambiar de pantalla.
   * @param token Token alternativo al del usuario logueado.
   * @param silent Marca la peticion como de fondo: su error no se muestra al usuario.
   * @returns Objeto de opciones listo para HttpClient.
   */
  headers(destroy = true, token?, silent = false) {
    let language = this.localValue.language;
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Accept-Language": language === "en" ? language : "es",
      Authorization: `Bearer ${token ? token : this.readToken()}`,
      destroy_petition: destroy ? "true" : "false",
      silent_error: silent ? "true" : "false"
    });

    return { headers: headers };
  }

  headersSoloAdmin(destroy = true) {
    let language = this.localValue.language;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Accept-Language": language === "en" ? language : "es",
      Authorization: `Bearer ${localStorage.getItem("token")}`
    });
    return { headers: headers };
  }

  headersMulipart() {
    let language = this.localValue.language;
    const headers = new HttpHeaders({
      "Accept-Language": language === "en" ? language : "es",
      Authorization: `Bearer ${this.readToken()}`
    });
    return { headers: headers };
  }

  readToken(isAdmin?) {
    const { mode, client } = this.localValue;
    if (isAdmin) {
      return localStorage.getItem("token");
    }
    if (mode === 1) {
      let localClient: string | null = client;
      if (localClient) {
        let client = <any>localClient;
        return client.token;
      }
    } else {
      return localStorage.getItem("token");
    }
  }

  validateError(e: any) {
    const { status, error, statusText } = e;

    const showError = (title: string, message: string = "") =>
      this.animation("error", this.capitalize(title), this.capitalize(message));

    const handleSpecificErrors = () => {
      if (error?.detail === "Invalid token" || statusText === "Unauthorized") {
        showError("Token inválido o sesión expirada");
        this.router.navigateByUrl("/login");
      }
    };

    const handleHttpErrors = () => {
      const messages: Record<number, string> = {
        400: "Solicitud incorrecta.",
        401: "No autorizado.",
        403: "Acceso prohibido.",
        404: "Recurso no encontrado.",
        500: "Error interno del servidor."
      };
      showError(`Error ${status}`, messages[status] || statusText || "Error desconocido.");
    };

    // NUEVO: Recorrer errores anidados
    const processNestedErrors = (obj: any, path: string[] = []) => {
      for (const key in obj) {
        if (!obj.hasOwnProperty(key)) continue;
        const value = obj[key];
        const currentPath = [...path, key];

        if (Array.isArray(value)) {
          value.forEach((msg) => {
            showError(currentPath.join("."), msg);
          });
        } else if (typeof value === "object" && value !== null) {
          processNestedErrors(value, currentPath);
        } else {
          showError(currentPath.join("."), value?.toString() || "Error desconocido");
        }
      }
    };

    try {
      console.log(status, error);

      if (error) {
        handleSpecificErrors();
        if (error.rows) return throwError(e);

        const details = error.user || error;

        if (typeof details === "object") {
          processNestedErrors(details); // NUEVO USO
        } else {
          showError("Error genérico", details.toString());
        }
      } else {
        if (status) handleHttpErrors();
      }
    } catch {
      showError("Error crítico", "Ocurrió un error inesperado.");
    }

    return throwError(e);
  }

  animation(type: "success" | "error" | "warning", text, title) {
    if (type) {
      this.toastr[type](title, text, { progressBar: true });
    }
  }

  /**
   * Capitalizes the first character of a string.
   * @param value The string to capitalize.
   * @returns The string with its first character in uppercase.
   */
  private capitalize(value: string): string {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  /*    this.setItem('preference', resp) */

  getPreferences(option?: "not_save", token?) {
    let local_token = token ? token : localStorage.getItem("token");

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Accept-language": "en-es",
      Authorization: `Bearer ${local_token}`
    });
    return this.http.get(`${Uris.PREFERENCES}user/`, { headers: headers }).pipe(
      map((resp: any) => {
        if (!option) {
          this.localValue = resp.personal_web;
          this.preference = resp;
          this.savedPersonalWeb = JSON.stringify(resp.personal_web);
        }
        /*       this.storageObs['preference'].emit(resp) */
        return resp;
      })
    );
  }

  editPreferences(value) {
    const { id } = this.preference;

    let token = localStorage.getItem("token");
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Accept-language": "en-es",
      Authorization: `Bearer ${token}`,
      destroy_petition: "false"
    });

    if (id && this.router.url !== "/login" && token) {
      this.lastPreferenceSave = Date.now();
      // Se toma la copia al enviar: localValue puede cambiar antes de la respuesta
      const sent = JSON.stringify(value);
      return this.http
        .patch(`${Uris.PREFERENCES}${id}/`, { personal_web: value }, { headers: headers })
        .pipe(
          map((resp: any) => {
            this.savedPersonalWeb = sent;
            return resp;
          })
        );
    }
  }

  changeFont(size: "reduce" | "add" | "default", initial_size = 0) {
    const root = document.documentElement;
    const limit = this.limit;

    let val_size = initial_size == 0 ? this.size : initial_size;
    initial_size !== 0 && (this.size = initial_size);
    let default_value = {
      h1: 2.25,
      h2: 1.625,
      h3: 1.4,
      h4: 0.938,
      h5: 0.875,
      p: 0.688,
      span: 0.688,
      button: 0.688,
      small: 0.6,
      b: 1,
      td: 0.688,
      th: 0.688,
      tr: 0.688,
      "form-control": 0.875
    };

    let results = () => {
      if (size === "default") {
        return initial_size;
      }
      if (this.size === -limit && size === "reduce") {
        return this.size;
      }
      if (this.size === limit && size === "add") {
        return this.size;
      }
      const val = size == "reduce" ? -1 : 1;
      if (size === "reduce" || size === "add") {
        return val_size + val;
      }
      return 0;
    };
    /* Sumar o restar cantidades */
    this.size = results();
    const validate_size = () => {
      if (size === "default") {
        /* debemos saber cuanto se debe de reducir o aumentar*/
        const validateacumm = (val) => {
          return val * 0.05;
        };

        /* Necesitamos saber por cuanto se multiplicara la fuente */
        const val_def = initial_size > 0 ? 1.1 : 0.95;
        if (initial_size < 0) {
          /* convertimos el valor a positivo */
          const convert = initial_size * -1;
          const acum = validateacumm(convert);
          return val_def - acum;
        }
        /* sumamos el % que deberia de estar */
        const acum_poss = validateacumm(initial_size);
        return val_def + acum_poss;
      }
      if (this.size === -limit && size === "reduce") {
        return 1;
      }
      if (this.size === limit && size === "add") {
        return 1;
      }
      return size === "reduce" ? 0.95 : 1.1;
    };
    let value = validate_size();

    Object.keys(default_value).forEach((prop) => {
      const fontSizeBase = getComputedStyle(root)
        .getPropertyValue(`--font-size-${prop}`)
        .replace("rem", "");
      const cal_value = Number(fontSizeBase) * value;
      const val_default = this.size === 0 ? default_value[prop] : cal_value;

      root.style.setProperty(`--font-size-${prop}`, `${val_default}rem`);
    });
    this.saveQueryFilters({
      font_size: this.size
    });
  }
}

export interface actions {
  type:
    | "listen.all"
    | "gateways.add_listener"
    | "gateways.remove_listener"
    | "waterwises.add_listener"
    | "waterwises.remove_listener"
    | "whitelist.set"
    | "whitelist.add"
    | "whitelist.remove"
    | "ping";
  data?: {
    waterwises?: Array<number>;
    gateways?: Array<number>;
    farm?: number;
    addresses?: Array<string>;
  };
}
export interface localValue {
  theme: "A" | "B" | "C" | "D" | "E" | "F" | "G" | "H";
  mode: number | "";
  client: any;
  farm_id: number | "";
  language: "spanish" | "en";
  isCollapsedText: boolean;
  sidebar_collapsed_sections?: string[];
  current_url: string;
  mode_table: boolean;
  keep_devices_selected: boolean;
  visible_filters?: { [key: string]: boolean } | string[];
  required_pond_testing: boolean;
  query_client: any | "";
  query_admin: any | "";
}

export type typePreference =
  | "TEM"
  | "PTEM"
  | "HUM"
  | "DO"
  | "EC"
  | "SAT"
  | "SG"
  | "SAL"
  | "TDS"
  | "PH"
  | "ORP"
  | "PRESSURE";
export interface preferenceChecks {
  id?: number;
  TEM: boolean;
  PTEM: boolean;
  HUM: boolean;
  DO: boolean;
  EC: boolean;
  SAT: boolean;
  SG: boolean;
  SAL: boolean;
  TDS: boolean;
  PH: boolean;
  ORP: boolean;
  PRESSURE: boolean;
}

export type SinglePreferenceCheck = {
  [K in keyof preferenceChecks]: { [P in K]: boolean };
}[keyof preferenceChecks];
